import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import TextScramble from '../components/TextScramble';
import { Eye, EyeOff } from 'lucide-react';

export default function RegisterPage() {
  const { register } = useCustomerAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', password: '', phone: '', address: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(form);
      navigate('/account');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  const set = (key, val) => setForm({...form, [key]: val});

  return (
    <div data-testid="register-page" className="min-h-screen pt-24 sm:pt-28 pb-24 flex items-center justify-center">
      <div className="w-full max-w-lg px-4">
        <div className="text-center mb-12">
          <TextScramble
            text="Cr&eacute;er un Compte"
            className="text-4xl sm:text-5xl tracking-tighter font-light text-black"
            delay={0.2}
          />
          <p className="mt-3 text-sm text-black/40">
            D&eacute;j&agrave; un compte ?{' '}
            <Link to="/login" data-testid="go-to-login" className="underline text-black/70 hover:text-black transition-colors">
              Se connecter
            </Link>
          </p>
        </div>

        {error && (
          <div data-testid="register-error" className="mb-6 p-3 text-sm text-center" style={{ backgroundColor: '#B800000D', color: '#B80000' }}>
            {error}
          </div>
        )}

        <form data-testid="register-form" onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 block mb-2">Pr&eacute;nom</label>
              <input
                data-testid="register-first-name"
                type="text"
                value={form.first_name}
                onChange={e => set('first_name', e.target.value)}
                required
                className="bg-transparent border-b border-black/20 focus:border-black outline-none py-3 w-full text-sm transition-colors"
                placeholder="Pr&eacute;nom"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 block mb-2">Nom</label>
              <input
                data-testid="register-last-name"
                type="text"
                value={form.last_name}
                onChange={e => set('last_name', e.target.value)}
                required
                className="bg-transparent border-b border-black/20 focus:border-black outline-none py-3 w-full text-sm transition-colors"
                placeholder="Nom de famille"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 block mb-2">Email</label>
            <input
              data-testid="register-email"
              type="email"
              value={form.email}
              onChange={e => set('email', e.target.value)}
              required
              className="bg-transparent border-b border-black/20 focus:border-black outline-none py-3 w-full text-sm transition-colors"
              placeholder="votre@email.com"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 block mb-2">Mot de passe</label>
            <div className="relative">
              <input
                data-testid="register-password"
                type={showPass ? 'text' : 'password'}
                value={form.password}
                onChange={e => set('password', e.target.value)}
                required
                minLength={6}
                className="bg-transparent border-b border-black/20 focus:border-black outline-none py-3 w-full text-sm transition-colors pr-10"
                placeholder="Minimum 6 caract&egrave;res"
              />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-0 top-3 text-black/30 hover:text-black/60">
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 block mb-2">T&eacute;l&eacute;phone</label>
            <input
              data-testid="register-phone"
              type="tel"
              value={form.phone}
              onChange={e => set('phone', e.target.value)}
              className="bg-transparent border-b border-black/20 focus:border-black outline-none py-3 w-full text-sm transition-colors"
              placeholder="+225 XX XX XX XX"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 block mb-2">Adresse</label>
            <textarea
              data-testid="register-address"
              value={form.address}
              onChange={e => set('address', e.target.value)}
              rows={2}
              className="bg-transparent border-b border-black/20 focus:border-black outline-none py-3 w-full text-sm transition-colors resize-none"
              placeholder="Votre adresse de livraison"
            />
          </div>

          <button
            data-testid="register-submit-btn"
            type="submit"
            disabled={loading}
            className="btn-primary w-full mt-8"
          >
            {loading ? 'Inscription...' : "S'inscrire"}
          </button>
        </form>
      </div>
    </div>
  );
}
